/**
 * The problem-report sheet's logic, apart from the screen: whether the sheet
 * is open (one sheet in the page, opened from two doors), and what happens
 * when the gardener presses Send.
 *
 * Sentry is only the postbox. The sheet (ReportProblemSheet) draws the form in
 * Grow's own look; this file decides what a press of Send does and returns one
 * of three outcomes the sheet draws. The sender is passed in (sendProblemReport
 * in sentry.ts) so the logic has no Sentry in it and is tested on its own.
 *
 * Who uses it: ReportProblemSheet (all of it), Header's account menu and
 * Today's "Let us know" (openReportProblem).
 *
 * @module reportProblem
 */

/** What a report carries besides the words: filterable tags for the owner. */
export type ReportTags = Record<string, string>;

/** One picture sent along with a report. */
export interface ReportAttachment {
  /** The file's name. */
  filename: string;
  /** The picture's bytes. */
  data: Uint8Array;
  /** Its type, such as image/jpeg. */
  contentType: string;
}

/** Sends one report to the postbox; resolves when it was received and rejects when it was not. */
export type SendReport = (params: { message: string; tags: ReportTags }, attachments: ReportAttachment[]) => Promise<unknown>;

/** What a press of Send came to. */
export type ReportOutcome = 'empty' | 'sent' | 'failed';

/** The source tag every report carries, so the owner can filter the reports from the crashes. */
export const REPORT_SOURCE = 'grow-report';

/** Whether the sheet is open. */
let sheetOpen = false;
/** Everyone listening for the sheet opening or closing. */
const listeners = new Set<() => void>();

/**
 * Tells every listener the open state changed.
 */
function emit(): void {
  listeners.forEach((listener) => listener());
}

/**
 * Opens the sheet. Both doors call this (the account menu's Report a problem
 * and Today's last line); the sheet itself is mounted once in the signed-in shell.
 */
export function openReportProblem(): void {
  sheetOpen = true;
  emit();
}

/**
 * Closes the sheet.
 */
export function closeReportProblem(): void {
  sheetOpen = false;
  emit();
}

/**
 * Listens for the sheet opening or closing (the shape useSyncExternalStore takes).
 *
 * @param listener - Called on every change.
 * @returns The function that stops listening.
 */
export function subscribeReportProblem(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Whether the sheet is open now.
 *
 * @returns True while it shows.
 */
export function isReportProblemOpen(): boolean {
  return sheetOpen;
}

/**
 * What a press of Send does: nothing for empty words, else send the report with
 * its picture (if any) and say whether the postbox received it.
 *
 * @param input.message - What the gardener wrote.
 * @param input.photo - The one picture, already shrunk, or null.
 * @param input.tags - The language, theme and page the report came from.
 * @param send - The sender (sendProblemReport in sentry.ts).
 * @returns 'empty' (nothing was sent), 'sent' or 'failed'. Never throws.
 */
export async function submitReport(
  input: { message: string; photo: File | null; tags: ReportTags },
  send: SendReport,
): Promise<ReportOutcome> {
  const message = input.message.trim();
  if (!message) return 'empty';
  try {
    const attachments: ReportAttachment[] = [];
    if (input.photo) {
      attachments.push({
        filename: input.photo.name || 'photo.jpg',
        data: new Uint8Array(await input.photo.arrayBuffer()),
        contentType: input.photo.type || 'image/jpeg',
      });
    }
    await send({ message, tags: { ...input.tags, source: REPORT_SOURCE } }, attachments);
    return 'sent';
  } catch (error) {
    // Reaching the postbox is what failed, so this cannot be reported to it; warn only.
    console.warn('Problem report did not send', error);
    return 'failed';
  }
}
